import React, {
  useState
   } from "react";
import { Outlet, 
  // useLocation 
} from "react-router-dom";
import NavigationHeader from "@/components/ui/navigation-header";
import { BreadcrumbItem } from "@/components/ui/breadcrumbs";
import { CartDrawer } from "@/components/business/cart/CartDrawer";

/**
 * Layout público para la tienda y páginas accesibles sin autenticación
 *
 * Incluye:
 * - Header unificado con navegación responsive
 * - Carrito de compras con badge
 * - Botón de inicio de sesión
 * - ThemeToggle para cambiar tema
 * - Breadcrumbs dinámicos
 * - Diseño mobile-first optimizado
 */
const PublicLayout: React.FC = () => {
  // const location = useLocation();
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Generar breadcrumbs dinámicamente basado en la ruta
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    if (pathSegments.length === 0) return [];

    const breadcrumbs: BreadcrumbItem[] = [];
    let currentPath = "";

    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Mapear segmentos a nombres legibles
      const segmentMap: Record<string, string> = {
        menu: "Menú",
        checkout: "Carrito",
        login: "Iniciar Sesión",
        register: "Registrarse",
        about: "Acerca de",
        contact: "Contacto",
        privacy: "Privacidad",
        terms: "Términos",
        payment: "Pago",
        success: "Éxito",
        error: "Error",
        "student-dashboard": "Dashboard del Estudiante",
      };

      const label =
        segmentMap[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1);

      breadcrumbs.push({
        label,
        href: index === pathSegments.length - 1 ? undefined : currentPath,
        current: index === pathSegments.length - 1,
      });
    });

    return breadcrumbs;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header Unificado */}
      <NavigationHeader
        variant="public"
        title="Rincón Chilenito"
        subtitle="Deliciosa comida chilena para llevar y delivery"
        breadcrumbs={getBreadcrumbs()}
        showCart={true}
        showThemeToggle={true}
        showUserMenu={true}
        userMenuVariant="compact"
        onCartClick={() => setIsCartOpen(true)}
      />

      {/* Contenido Principal */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-sm mt-auto">
        <div className="container mx-auto px-4 py-8 sm:py-12">
          {/* Contenido principal del footer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 text-center lg:text-left">
            {/* Información de la empresa */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 justify-center lg:justify-start">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">
                    C
                  </span>
                </div>
                <h3 className="font-bold text-lg text-card-foreground">
                  Rincón Chilenito
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Deliciosa comida chilena para llevar y delivery. Sabores
                auténticos de nuestra tierra.
              </p>
            </div>

            {/* Enlaces rápidos */}
            <div className="space-y-4">
              <h3 className="font-semibold text-card-foreground text-base">
                Enlaces Rápidos
              </h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <a
                    href="/store"
                    className="text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2 group justify-center lg:justify-start"
                  >
                    <span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Nuestro Menú
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    className="text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2 group justify-center lg:justify-start"
                  >
                    <span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Acerca de Nosotros
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2 group justify-center lg:justify-start"
                  >
                    <span className="w-1 h-1 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    Contacto
                  </a>
                </li>
              </ul>
            </div>

            {/* Información de contacto */}
            <div className="space-y-4">
              <h3 className="font-semibold text-card-foreground text-base">
                Contacto
              </h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-2 justify-center lg:justify-start">
                  <span className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs">📞</span>
                  </span>
                  +56 9 1234 5678
                </li>
                <li className="flex items-center gap-2 justify-center lg:justify-start">
                  <span className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs">📧</span>
                  </span>
                  info@chilenito.cl
                </li>
                <li className="flex items-center gap-2 justify-center lg:justify-start">
                  <span className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs">📍</span>
                  </span>
                  Calama, Chile
                </li>
              </ul>
            </div>

            {/* Horarios */}
            <div className="space-y-4">
              <h3 className="font-semibold text-card-foreground text-base">
                Horarios
              </h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-center gap-2 justify-center lg:justify-start">
                  <span className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs">🕒</span>
                  </span>
                  <div className="text-center lg:text-left">
                    <div className="font-medium">Lun - Vie</div>
                    <div>8:00 - 22:00</div>
                  </div>
                </li>
                {/* <li className="flex items-center gap-2 justify-center lg:justify-start">
                  <span className="w-4 h-4 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary text-xs">🕒</span>
                  </span>
                  <div className="text-center lg:text-left">
                    <div className="font-medium">Sáb - Dom</div>
                    <div>9:00 - 23:00</div>
                  </div>
                </li> */}
              </ul>
            </div>
          </div>

          {/* Línea divisoria */}
          <div className="border-t border-border/50 mt-8 pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                &copy; 2025 Rincón Chilenito. Todos los derechos reservados.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <a
                  href="/privacy"
                  className="text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  Privacidad
                </a>
                <a
                  href="/terms"
                  className="text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  Términos
                </a>
              </div>
            </div>
          </div>

          {/* Consola de desarrollo */}
          <div className="mt-6 p-3 sm:p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-lg border border-border/50">
            <div className="font-mono text-xs space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-xs sm:text-xs">
                  $ deploy --version=2.0.0 --target=production --region=latam
                </span>
              </div>
              <div className="flex items-center gap-2 text-green-600">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-xs sm:text-xs">
                  &gt; build: success | tests: passed | deployment: complete
                </span>
              </div>
              <div className="flex items-center gap-2 text-blue-600">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-xs sm:text-xs">
                  &gt; Site is live at{" "}
                  <a
                    href="https://shinsekai.cl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline transition-colors duration-200"
                  >
                    https://shinsekai.cl
                  </a>{" "}
                  (commit: #g7t42k)
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default PublicLayout;
